/**
 * Shared display labels and status→tone mapping.
 *
 * Case status and issue type are stored as snake_case enums; every admin screen
 * renders them through these maps so one case never reads two different ways.
 */

export const issueTypeLabels = {
  not_arrived: 'Not arrived yet',
  delivered_not_received: 'Delivered but not received',
  damaged: 'Damaged',
  missing_items: 'Missing items',
  unsafe_place: 'Left in unsafe place'
};

export const caseStatusLabels = {
  submitted: 'Submitted',
  evidence_prepared: 'Evidence prepared',
  merchant_reviewed: 'Merchant reviewed',
  merchant_responded: 'Merchant responded',
  no_merchant_response: 'No merchant response',
  closed: 'Closed',
  system_error: 'System error'
};

/** Reserved status colours: green = resolved, amber = waiting, red = fault. */
export const caseStatusTone = {
  submitted: 'warning',
  evidence_prepared: 'warning',
  merchant_reviewed: 'info',
  merchant_responded: 'info',
  no_merchant_response: 'danger',
  closed: 'success',
  system_error: 'danger'
};

export const paymentStatusTone = {
  completed: 'success',
  pending: 'warning',
  failed: 'danger'
};

/** Who raised the case: the customer via the public form, or the merchant on their behalf from the portal. */
export const createdByLabels = {
  customer: 'Customer',
  merchant: 'Merchant'
};

export const createdByTone = {
  customer: 'neutral',
  merchant: 'info'
};

/**
 * Which billing model actually counted this case, stamped when it was
 * billed — not the merchant's current plan, which can move on afterwards.
 * Null/undefined means not billed yet (no action taken, no PDF downloaded).
 */
export const billedAsLabels = {
  pilot: 'Pilot',
  subscription: 'Subscription',
  // Still billing model 'subscription' — this specific case was paid for
  // individually, over the plan's included allowance, not covered by it.
  subscription_overage: 'Subscription (overage)',
  pay_per_case: 'Pay per case',
  free: 'Free case'
};

/**
 * billedAs for a case counted before that field existed can't be recovered
 * in general — but payPerCasePaidAt is set ONLY by a pay-per-case payment,
 * so a case that has it can still be labelled correctly. Anything else
 * stays honestly unlabelled rather than guessed: cases counted the same way
 * (billingCountedAt set, no billedAs) were billed under a mix of pilot,
 * subscription and the free first case too, not all pay-per-case.
 */
export const inferBilledAs = (caseItem) =>
  caseItem?.billedAs || (caseItem?.payPerCasePaidAt ? 'pay_per_case' : null);

export const billedAsTone = {
  pilot: 'info',
  subscription: 'success',
  subscription_overage: 'warning',
  pay_per_case: 'success',
  free: 'neutral'
};

/**
 * Who raised a case. A merchant-raised case names the teammate who did it
 * (createdByUser) — "your team" alone doesn't help an account with agents
 * work out who to ask about it.
 */
export const raisedByLabel = (caseItem) => {
  if (caseItem?.createdBy !== 'merchant') return 'Customer';
  return caseItem.createdByUser?.name || 'Your team';
};

export const raisedByTone = (caseItem) =>
  caseItem?.createdBy === 'merchant' ? 'info' : 'neutral';

/** Transaction ledger entry types — written by the Stripe webhook as each payment is confirmed. */
export const transactionTypeLabels = {
  pay_per_case: 'Pay per case',
  case_overage: 'Case overage',
  subscription_created: 'Subscription created',
  subscription_renewed: 'Subscription renewed',
  subscription_switched: 'Plan switched',
  overage_invoice: 'Overage invoice paid'
};

export const transactionTypeTone = {
  pay_per_case: 'success',
  case_overage: 'warning',
  subscription_created: 'success',
  subscription_renewed: 'info',
  subscription_switched: 'info',
  overage_invoice: 'warning'
};

/** Stripe's own subscription.status, mirrored locally by the webhook. */
export const subscriptionStatusLabels = {
  active: 'Active',
  canceled: 'Canceled',
  past_due: 'Past due',
  trialing: 'Trialing'
};

export const subscriptionStatusTone = {
  active: 'success',
  canceled: 'neutral',
  past_due: 'danger',
  trialing: 'info'
};

/**
 * Merchant decisions, worded as the customer already sees them in the
 * notification email (emailService.js) — one vocabulary, not three.
 */
export const actionLabels = {
  refund: 'Approve Refund',
  reship: 'Approve Reship',
  escalate: 'Escalate to Courier',
  reject: 'Reject'
};

/**
 * Decision colours. Reject is the only red one: it is the outcome that leaves
 * the customer without resolution. Escalate is amber because the case is still
 * open and waiting on the courier.
 */
export const actionTone = {
  refund: 'success',
  reship: 'info',
  escalate: 'warning',
  reject: 'danger'
};

/** Email delivery wording — 'processed' means accepted, not yet delivered. */
export const emailStatusLabels = {
  processed: 'Sent',
  delivered: 'Delivered',
  opened: 'Opened',
  clicked: 'Clicked',
  bounced: 'Bounced',
  dropped: 'Dropped',
  deferred: 'Deferred'
};

export const emailStatusTone = {
  opened: 'success',
  clicked: 'success',
  delivered: 'info',
  processed: 'info',
  bounced: 'danger',
  dropped: 'danger',
  deferred: 'warning'
};

export const humanise = (value) =>
  typeof value === 'string' ? value.replace(/_/g, ' ') : value;

export const formatNumber = (value) =>
  typeof value === 'number' ? value.toLocaleString('en-GB') : value;

export const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    : '—';

export const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—';
